// #region Imports
import AddBoxIcon from "@mui/icons-material/AddBox";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { CheckboxProps, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { alpha, keyframes, styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { PaginationHeaders } from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import NumberBadge from "_shared/_views/badges/number-badge";
import RowLink from "_shared/_views/tables/row-link";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import { debounce, uniq } from "lodash";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_Updater,
  type MRT_VisibilityState as VisibilityState,
} from "material-react-table";
import {
  ToolbarButtons,
  searchFieldSpacing,
} from "_shared/_views/tables/table-toolbar";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import useSessionUser from "permissions/session/use-session-user";
import { ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";

import { useSession } from "users/__session/session-context";
import FeeScheduleLink from "work/fee-schedule-templates/_views/links/fee-schedule-template-link";
import ProposalRequestDraftDialog from "work/proposal-requests/_views/proposal-request-draft-dialog";
import OnboardingProposalStatePage from "work/proposals/_views/onboarding/onboarding-proposal-state-page";
import {
  HumanReadableProposalStatus,
  ProposalBoxType,
  ProposalOrderField,
} from "work/proposals/__constants/proposal-lifecycle";
import {
  useBulkDeleteProposalsMutation,
  useGetActiveProposalsCountQuery,
  useGetArchivedProposalsCountQuery,
  useGetDraftProposalsCountQuery,
  useGetInboxProposalsCountQuery,
  useGetProposalsQuery,
} from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import {
  updateServerProposal,
  getLastSuccessfulProposalQueryByInterface,
  getProposalQueryByInterface,
  getProposalsByQuery,
  isProposalLoadIssue,
  ProposalLoadIssue,
  updateProposalInterfaceQuery
} from "work/proposals/_store/proposals-redux-slice";
import CreatorLink from "work/proposals/_views/_links/creator-link/creator-link";
import {
  getProposalTableContext,
  ProposalTableContext,
} from "work/proposals/_views/proposal-table-context";
import { proposalListDestination } from "work/proposals/_views/proposal-list-destination";
import {
  isPageTab,
  openingPageTab,
  PageTab,
  readStoredPageTab,
  storePageTab,
} from "work/proposals/_views/proposals-page-tabs";
import Proposal from "work/proposals/proposal";
import VendorTeamLink from "work/work-agreements/teams/vendor-team/_views/vendor-team-link";
// #endregion

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flex: "1 1 0",
  flexDirection: "column",
  maxWidth: "100%",
  minWidth: 0,
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));
const TableTitleContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
}));
const ProposalTabs = styled(Tabs)(() => ({
  flexShrink: 0,
  minWidth: 0,
  maxWidth: "100%",
  "& .MuiTabs-scroller": {
    minWidth: 0,
  },
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const FilterButtons = styled("div")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  marginTop: 0,
  minWidth: 0,
  width: "auto",
  [theme.breakpoints.down("md")]: {
    display: "grid",
    gridAutoColumns: "auto",
    gridAutoFlow: "column",
    marginTop: theme.spacing(1),
    width: "100%",
  },
  "& > .MuiChip-root": {
    [theme.breakpoints.down("md")]: {
      justifyContent: "center",
      marginBottom: 0,
      maxWidth: "none",
      width: "100%",
    },
  },
}));
const MobileToolbarButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
  minWidth: 0,
  width: "100%",
  "& .MuiTextField-root": {
    flex: "1 1 auto",
    minWidth: 0,
  },
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const RowTitle = styled(Typography)(() => ({
  display: "block",
  flex: "1 1 auto",
  fontSize: "1.1em",
  fontWeight: "600",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const EllipsisCell = styled(Typography)(() => ({
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const EllipsisSpan = styled("span")(() => ({
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const failedOperationKeyFrame = keyframes`
  0% { background-color: rgba(255, 100, 100, 0.1); }
  50% { background-color: rgba(255, 100, 100, 0.3); }
  100% { background-color: rgba(255, 100, 100, 0.1); }
`;
// #endregion

type ProposalTableContextStyleConfig = {
  borderPalette?: "warning";
  palette: "error" | "info" | "success" | "text";
  backgroundOpacity: number;
  hoverOpacity: number;
  selectedOpacity: number;
  selectedHoverOpacity: number;
};

const proposalTableContextStyleConfig: Record<ProposalTableContext, ProposalTableContextStyleConfig> = {
  [ProposalTableContext.ChangesAwaitingYourApproval]: {
    palette: "error",
    backgroundOpacity: 0.08,
    hoverOpacity: 0.13,
    selectedOpacity: 0.17,
    selectedHoverOpacity: 0.22,
  },
  [ProposalTableContext.ReviewerAwaitingYourApproval]: {
    palette: "info",
    backgroundOpacity: 0.1,
    hoverOpacity: 0.16,
    selectedOpacity: 0.2,
    selectedHoverOpacity: 0.26,
  },
  [ProposalTableContext.ReadyToHire]: {
    palette: "success",
    backgroundOpacity: 0.1,
    hoverOpacity: 0.16,
    selectedOpacity: 0.2,
    selectedHoverOpacity: 0.26,
  },
  [ProposalTableContext.NoAction]: {
    palette: "text",
    backgroundOpacity: 0.04,
    hoverOpacity: 0.07,
    selectedOpacity: 0.1,
    selectedHoverOpacity: 0.13,
  },
};

const columnHelper = createMRTColumnHelper<Proposal | ProposalLoadIssue>();
const proposalBoxTypes = Object.values(ProposalBoxType) as string[];
const statusFilterOptions = Object.entries(HumanReadableProposalStatus).map(([key, value]) => ({
  value: key,
  label: value,
}));

type SortingState = Array<{ id: string; desc: boolean }>;
type ProposalTableSearchState = {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
  pagination: PaginationState;
};

function isProposalBoxType(value: string | null): value is ProposalBoxType {
  return value !== null && proposalBoxTypes.includes(value);
}

export default function ProposalsPage() {
  // #region Refs
  const isDialogOpenRef = useRef(false);

  // #region Hooks
  const { openDialog, closeDialog, numDialogs } = useDialog();
  const { pageTab: pageTabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionUser = useSessionUser();
  const location = useLocation();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isCompactTeamColumn = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const isMinimalTeamColumn = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const teamColumnDisplayMode: "minimal" | "compact" | "full" = isMinimalTeamColumn
    ? "minimal"
    : isCompactTeamColumn
      ? "compact"
      : "full";
  const teamColumnSize = isMinimalTeamColumn ? 70 : isCompactTeamColumn ? 150 : 260;
  const [userColumnVisibility, setUserColumnVisibility] = useState<VisibilityState>({});

  // #region Route
  const isRequestRoute = location.pathname.startsWith("/proposals/request");
  const pageTab = isPageTab(pageTabParam) ? pageTabParam : undefined;
  const effectivePageTab = pageTab ?? PageTab.Active;

  // #region Opening tab
  // Which tab a visit to `/proposals` with no tab in it opens. A tab in the URL always wins, and a
  // choice the reader made earlier this session wins over anything the counts suggest; only when
  // neither has an answer does the page work it out from how full the tabs are -- see
  // `openingPageTab`, which owns the rules and the reason for each.
  const [explicitPageTab] = useState(readStoredPageTab);
  const activeProposalCountQuery = useGetActiveProposalsCountQuery({ session });
  const draftProposalCountQuery = useGetDraftProposalsCountQuery({ session });
  const archivedProposalCountQuery = useGetArchivedProposalsCountQuery({ session });
  const isDerivingPageTab =
    !isRequestRoute && !pageTab && !session.isRestrictedOnboarding && explicitPageTab === undefined;
  // The tie-break is a request of its own, so it is only asked for once the two tabs are actually
  // tied -- which is the one case that cannot be settled by the counts the tab badges already need.
  const areWorkingTabsTied =
    isDerivingPageTab && (activeProposalCountQuery.data ?? 0) > 0 && (draftProposalCountQuery.data ?? 0) > 0;
  const inboxProposalCountQuery = useGetInboxProposalsCountQuery({ session }, { skip: !areWorkingTabsTied });
  // A count that failed is not a count of zero, and guessing from it would open the emptier tab.
  // Falling back to `Active` gives up the choice rather than getting it wrong, and `Active` is what
  // this page opened on before it chose at all.
  const areTabCountsUnavailable =
    activeProposalCountQuery.isError || draftProposalCountQuery.isError || inboxProposalCountQuery.isError;
  const derivedPageTab = areTabCountsUnavailable
    ? PageTab.Active
    : openingPageTab({
      active: activeProposalCountQuery.data,
      drafts: draftProposalCountQuery.data,
      activeWaitingOnYou: inboxProposalCountQuery.data,
    });
  // A restricted onboarding session has one tab and no tab bar, so there is nothing to choose.
  const pageTabToOpen = session.isRestrictedOnboarding ? PageTab.Active : (explicitPageTab ?? derivedPageTab);
  /**
   * The page is on `/proposals` and does not know yet which tab that means.
   *
   * Everything downstream reads this rather than opening `Active` on spec: the table shows its
   * skeletons, the list query is not registered, and no tab is marked selected, so the answer that
   * arrives a moment later is drawn once instead of replacing one the reader had started on.
   */
  const isChoosingPageTab = !isRequestRoute && !pageTab && pageTabToOpen === undefined;
  // #endregion

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  // Creating a proposal needs entity-wide access, a strictly higher bar than the read access that
  // gets someone onto this page. Saying so on the button beats letting them build a whole proposal
  // and meet the API's 403 at Save.
  // The super-user exclusion that used to sit on each button has moved inside `canCreateProposals`,
  // where it is stated once and explained: this view asks what the session may do, not who it is.
  const canCreateProposal = sessionUser?.canCreateProposals() ?? false;
  const createProposalTooltip = !sessionPermissions && !sessionPermissionsError
    ? "Loading proposal permissions..."
    : canCreateProposal
      ? "Create Proposal"
      : "Creating a proposal requires entity-wide proposal access. Ask an administrator at your firm.";
  // #endregion

  const interfaceName = `proposalsPage:${effectivePageTab}`;
  const proposalsQueryParams = useSelector(getProposalQueryByInterface(interfaceName));

  const paginatedProposalsJson = useSelector(getProposalsByQuery(proposalsQueryParams));
  const paginatedProposals = useMemo(() => {
    if (!paginatedProposalsJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const proposalJson of paginatedProposalsJson.data) {
      try {
        if (isProposalLoadIssue(proposalJson)) {
          deserializedData.push(proposalJson);
        } else {
          const proposal = Proposal.fromJSON({
            json: proposalJson,
            currentUser: session.user!,
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", proposalJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in proposalJson ? (Guid.fromJSON(proposalJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedProposalsJson, session.user]);

  const proposalsAPIQuery = useGetProposalsQuery(
    proposalsQueryParams && !isChoosingPageTab ? { query: proposalsQueryParams, interfaceName } : skipToken,
  );

  // TODO: make sure to namespace by tab to avoid showing incorrect counts/data when switching tabs
  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulProposalQueryByInterface(interfaceName));

  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const item of lastSuccessfulQueryResponseJson.data) {
      try {
        if (isProposalLoadIssue(item)) {
          deserializedData.push(item);
        } else {
          const proposal = Proposal.fromJSON({
            json: item,
            currentUser: session.user!,
            options: { withRedline: false },
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", item);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in item ? (Guid.fromJSON(item.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [lastSuccessfulQueryResponseJson, session.user]);

  // While the opening tab is undecided the page holds no rows at all, cached ones included: the
  // Active interface's last response is still in the store from an earlier visit, and drawing it
  // now is exactly the table-under-the-reader this page was asked to stop doing.
  const tableProposals = isChoosingPageTab ? undefined : (paginatedProposals ?? lastSuccessfulQueryResponse);
  const tableProposalsHeaders = isChoosingPageTab
    ? undefined
    : (paginatedProposalsJson?.headers ?? lastSuccessfulQueryResponseJson?.headers);

  // #region Creator hydration
  // Every creator this page will name, asked about in one lookup.
  //
  // The Creator cell reads its name out of the Individuals store, so something has to put it there,
  // and it cannot be the cell: a page of rows would then put one `individuals-by-userids` request on
  // the wire per row. This is the same shape `proposal-client-team-view.tsx` hydrates a roster with
  // — derive the ids, subtract what the store already holds, ask only for the remainder — lifted to
  // the table, which is the only place that can see the whole page at once.
  //
  // The two cells that never name anybody stay out of the set: the caller's own rows read "You",
  // and a restricted-onboarding viewer sees "AttorneyHub" on every row, so asking for either would
  // spend a request on a name no cell can render.
  const creatorUserIds = useMemo(() => {
    const userIds = new Set<string>();
    if (session.isRestrictedOnboarding) return userIds;
    for (const row of tableProposals ?? []) {
      if (isProposalLoadIssue(row)) continue;
      // `creator` is optional at runtime for the same reason the cell below guards it, even though
      // the type says otherwise. A row whose JSON carries no creator actually fails deserialization
      // and arrives here as a load issue, so this is a guard rather than a reachable state.
      const creatorUserId = row.creator?.userId.value;
      if (!creatorUserId) continue;
      if (creatorUserId === session.user?.id?.value) continue;
      userIds.add(creatorUserId);
    }
    return userIds;
  }, [session.isRestrictedOnboarding, session.user?.id?.value, tableProposals]);
  const cachedCreators = useSelector(getIndividualsByUserIds(creatorUserIds));
  const missingCreatorUserIds = useMemo(() => {
    // Keyed on userId alone, exactly as the roster is: `byUserId` in individuals-redux-slice.ts
    // holds one row per user id, and a name is a personal attribute rather than a per-entity one.
    // Keying on the compound `userId:legalEntityId` pair would only manufacture misses whenever a
    // cached row's legalEntityId disagreed with the proposal membership's.
    const cachedUserIds = new Set(cachedCreators.map((individual) => individual.userId.value));
    return new Set(Array.from(creatorUserIds).filter((userId) => !cachedUserIds.has(userId)));
  }, [cachedCreators, creatorUserIds]);
  useGetIndividualsByUserIdsQuery(missingCreatorUserIds.size > 0 ? { userIds: missingCreatorUserIds } : skipToken);
  // #endregion

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const searchParamsKey = searchParams.toString();
  const queryGlobalFilter = getGlobalFilterFromSearchParams(searchParams);
  const [globalFilterDraft, setGlobalFilterDraft] = useState(() => ({
    searchParamsKey,
    value: queryGlobalFilter,
  }));
  const globalFilter =
    globalFilterDraft.searchParamsKey === searchParamsKey ? globalFilterDraft.value : queryGlobalFilter;
  const columnFilters = useMemo(() => getColumnFiltersFromSearchParams(searchParams), [searchParams]);
  const sorting = useMemo(() => getSortingFromSearchParams(searchParams), [searchParams]);
  const pagination = useMemo(() => getPaginationFromSearchParams(searchParams), [searchParams]);

  const [bulkDeleteProposalsQuery, bulkDeleteProposalsResult] = useBulkDeleteProposalsMutation();
  const [prevBulkDeleteFailureIdValues, setPrevBulkDeleteFailureIdValues] = useState<string[]>([]);
  const [bulkDeleteFailureIdValues, setBulkDeleteFailureIdValues] = useState<string[]>([]);

  useEffect(() => {
    if (isRequestRoute || pageTab || !pageTabToOpen) return;
    navigate(`/proposals/${pageTabToOpen}`, { replace: true });
  }, [isRequestRoute, navigate, pageTab, pageTabToOpen]);

  useEffect(() => {
    if (!session.isRestrictedOnboarding) return;
    if (pageTab && pageTab !== PageTab.Active) {
      navigate(`/proposals/${PageTab.Active}`, { replace: true });
    }
  }, [navigate, pageTab, session.isRestrictedOnboarding]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;
    if (isChoosingPageTab) return;

    let boxType: ProposalBoxType | undefined = undefined;
    const boxTypeParam = searchParams.get("boxType");
    if (isProposalBoxType(boxTypeParam)) boxType = boxTypeParam;
    if (!boxType && effectivePageTab === PageTab.Active) boxType = ProposalBoxType.AllActive;
    else if (!boxType && effectivePageTab === PageTab.Drafts) boxType = ProposalBoxType.Draft;
    else if (!boxType && effectivePageTab === PageTab.Archived) boxType = ProposalBoxType.Archive;

    const filterParameters = ProposalFilterParameters.fromSearchParams(searchParams, session, boxType);
    const orderParameters = ProposalOrderParameters.fromSearchParams(searchParams);

    const newQuery = new ProposalQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });
    if (proposalsQueryParams && newQuery.isEqualTo(proposalsQueryParams)) return;
    dispatch(
      updateProposalInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [effectivePageTab, isChoosingPageTab, session.user?.legalEntityId.value, session, searchParams, proposalsQueryParams, dispatch, interfaceName]);

  const updateSearchParamsFromTableState = useCallback(
    (nextState: ProposalTableSearchState) => {
      const newSearchParams = getSearchParamsFromTableState(nextState, searchParams);
      if (newSearchParams.toString() === searchParams.toString()) return;
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const commitGlobalFilterSearchParams = useCallback(
    (nextGlobalFilter: string) => {
      setSearchParams((currentSearchParams) => {
        const newSearchParams = getSearchParamsFromTableState(
          {
            globalFilter: nextGlobalFilter,
            columnFilters: getColumnFiltersFromSearchParams(currentSearchParams),
            sorting: getSortingFromSearchParams(currentSearchParams),
            pagination: getPaginationFromSearchParams(currentSearchParams),
          },
          currentSearchParams,
        );

        return newSearchParams.toString() === currentSearchParams.toString() ? currentSearchParams : newSearchParams;
      });
    },
    [setSearchParams],
  );

  const debouncedCommitGlobalFilterSearchParams = useMemo(
    () => debounce(commitGlobalFilterSearchParams, 350),
    [commitGlobalFilterSearchParams],
  );

  useEffect(() => {
    return () => debouncedCommitGlobalFilterSearchParams.cancel();
  }, [debouncedCommitGlobalFilterSearchParams]);

  const setGlobalFilter = useCallback(
    (updater: MRT_Updater<string>) => {
      setGlobalFilterDraft((previousDraft) => {
        const previousGlobalFilter =
          previousDraft.searchParamsKey === searchParamsKey ? previousDraft.value : queryGlobalFilter;
        const nextGlobalFilter = applyUpdater(updater, previousGlobalFilter);
        debouncedCommitGlobalFilterSearchParams(nextGlobalFilter);
        return {
          searchParamsKey,
          value: nextGlobalFilter,
        };
      });
    },
    [debouncedCommitGlobalFilterSearchParams, queryGlobalFilter, searchParamsKey],
  );

  const handlePageTabChange = useCallback(
    (_event: SyntheticEvent, newTab: PageTab) => {
      storePageTab(newTab);
      debouncedCommitGlobalFilterSearchParams.cancel();
      navigate(`/proposals/${newTab}`);
    },
    [debouncedCommitGlobalFilterSearchParams, navigate],
  );

  const setColumnFilters = useCallback(
    (updater: MRT_Updater<ColumnFiltersState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters: applyUpdater(updater, columnFilters),
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setSorting = useCallback(
    (updater: MRT_Updater<SortingState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting: applyUpdater(updater, sorting),
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setPagination = useCallback(
    (updater: MRT_Updater<PaginationState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting,
        pagination: applyUpdater(updater, pagination),
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  useEffect(() => {
    if (proposalsAPIQuery.isError) {
      enqueueSnackbar("Error loading proposal table", { variant: "error" });
    }
  }, [proposalsAPIQuery.isError]);

  useEffect(() => {
    if (!location.pathname.startsWith("/proposals/request")) return;
    if (isDialogOpenRef.current || numDialogs > 0) return;

    isDialogOpenRef.current = true;

    openDialog({
      title: "Request Proposal",
      resetStack: true,
      MuiProps: {
        fullWidth: true,
        maxWidth: "sm",
      },
      onClose: () => {
        isDialogOpenRef.current = false;
        navigate("/proposals/drafts", { replace: true });
      },
      component: (
        <ProposalRequestDraftDialog
          searchParams={new URLSearchParams(searchParams)}
          onCancel={() => {
            isDialogOpenRef.current = false;
            navigate("/proposals/drafts", { replace: true });
            closeDialog();
          }}
          onProposalSubmitted={(submittedProposal: Proposal) => {
            navigate(`/proposals/active/revise/${submittedProposal.id}`, {
              replace: true
            });
            dispatch(updateServerProposal(submittedProposal.toJSON()));
            closeDialog();
          }}
        />
      ),
    });
  }, [closeDialog, dispatch, location.pathname, navigate, numDialogs, openDialog, searchParams]);

  const handleBulkDraftProposalDelete = useCallback(async () => {
    setPrevBulkDeleteFailureIdValues([...bulkDeleteFailureIdValues]);
    const selectedRowIdValues = Object.keys(rowSelection).filter((id) => rowSelection[id]);

    const response = await confirm({
      title: "Delete Proposals?",
      message: `Are you sure you want to delete ${selectedRowIdValues.length} proposal(s)?`,
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const failedDeletionsResponse: Array<string> =
        (await bulkDeleteProposalsQuery({
          ids: selectedRowIdValues,
        }).unwrap()) ?? [];
      if (failedDeletionsResponse.length > 0) {
        setBulkDeleteFailureIdValues(uniq(bulkDeleteFailureIdValues.concat(failedDeletionsResponse)));
      }

      if (failedDeletionsResponse.length === selectedRowIdValues.length) {
        const error = bulkDeleteProposalsResult.error;
        console.error(error);
        enqueueSnackbar("Failed to delete proposals", { variant: "error" });
        setRowSelection({});
        return;
      }

      if (failedDeletionsResponse.length > 0) {
        enqueueSnackbar(`${failedDeletionsResponse.length} proposals could not be deleted`, {
          variant: "warning",
        });
      } else {
        enqueueSnackbar(`${selectedRowIdValues.length} Proposals deleted successfully`, { variant: "success" });
      }
      setRowSelection({});
      if (proposalsQueryParams) {
        dispatch(
          updateProposalInterfaceQuery({
            interfaceName: interfaceName,
            query: proposalsQueryParams,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete proposals", { variant: "error" });
    }
  }, [
    bulkDeleteFailureIdValues,
    bulkDeleteProposalsQuery,
    bulkDeleteProposalsResult.error,
    confirm,
    dispatch,
    interfaceName,
    proposalsQueryParams,
    rowSelection
  ]);

  const renderStatusColumn = useCallback(
    (table: MRT_TableInstance<Proposal | ProposalLoadIssue>, proposal: Proposal | ProposalLoadIssue): ReactNode => {
      if (table.getState().isLoading) return;
      if (isProposalLoadIssue(proposal)) {
        return (
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: "4000px",
                  whiteSpace: "normal",
                },
              },
            }}
            title={`Our staff has been notified. Reference id: ${proposal.id.value}`}
          >
            <EllipsisSpan>Error Loading Proposal</EllipsisSpan>
          </Tooltip>
        );
      }

      const displayStatus = proposal.displayStatus;
      if (proposal.id?.value && bulkDeleteFailureIdValues.includes(proposal.id.value)) {
        return (
          <>
            <EllipsisSpan title={displayStatus}>{displayStatus}</EllipsisSpan>
            <br />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "0.7rem",
                color: "error.main",
              }}
            >
              Cannot delete this proposal
            </Box>
          </>
        );
      }

      return <EllipsisCell title={displayStatus}>{displayStatus}</EllipsisCell>;
    },
    [bulkDeleteFailureIdValues],
  );

  const renderCreatorColumn = useCallback(
    (table: MRT_TableInstance<Proposal | ProposalLoadIssue>, proposal: Proposal | ProposalLoadIssue) => {
      if (table.getState().isLoading) return;
      if ((isProposalLoadIssue(proposal) || !proposal.creator)) return <Icon>remove</Icon>;
      if (proposal.creator.userId.isEqualTo(session.user?.id)) {
        return "You";
      }
      if (session.isRestrictedOnboarding) {
        return "AttorneyHub";
      }
      return <CreatorLink creator={proposal.creator} />;
    },
    [session.isRestrictedOnboarding, session.user?.id],
  );

  const renderTeamColumn = useCallback(
    (table: MRT_TableInstance<Proposal | ProposalLoadIssue>, proposal: Proposal | ProposalLoadIssue) => {
      if (table.getState().isLoading) return;
      if (isProposalLoadIssue(proposal) || !proposal.vendorTeam) return <Icon>remove</Icon>;
      if (session.isRestrictedOnboarding) return "AttorneyHub team";
      return (
        <VendorTeamLink
          displayMode={teamColumnDisplayMode}
          proposal={proposal}
          session={session}
        />
      );
    },
    [session, teamColumnDisplayMode],
  );

  const renderFeeScheduleColumn = useCallback((table: MRT_TableInstance<Proposal | ProposalLoadIssue>, proposal: Proposal | ProposalLoadIssue) => {
    if (table.getState().isLoading) return;
    if (isProposalLoadIssue(proposal) || !proposal.feeSchedule) return <Icon>remove</Icon>;
    if (session.isRestrictedOnboarding) {
      const feeCount = Array.isArray(proposal.feeSchedule) ? proposal.feeSchedule.length : 0;
      return feeCount === 0 ? "Fee schedule" : `${feeCount} ${feeCount === 1 ? "Fee" : "Fees"}`;
    }
    return <FeeScheduleLink proposal={proposal} />;
  }, [session.isRestrictedOnboarding]);

  const currentBoxType = searchParams.get("boxType");
  const handleChangeBoxType = useCallback(
    (newBoxType: ProposalBoxType) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (currentBoxType === newBoxType) {
          params.delete("boxType");
        } else {
          params.set("boxType", newBoxType.valueOf());
        }
        return params;
      });
    },
    [currentBoxType, setSearchParams],
  );

  // Where a row goes, in one place, because the row and the link in its name cell have to agree —
  // and where a proposal opens is not a simple id. `proposalListDestination` reads what this user
  // may do with this proposal from the tab it is listed on.
  const proposalDestination = useCallback(
    (proposal: Proposal | ProposalLoadIssue): string | undefined => {
      if (isProposalLoadIssue(proposal) || !proposal.id) return undefined;
      return proposalListDestination(proposal, {
        listTab: effectivePageTab,
        userId: session.user?.id,
        isRestrictedOnboarding: session.isRestrictedOnboarding,
      });
    },
    [effectivePageTab, session.isRestrictedOnboarding, session.user?.id],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: "Status",
        id: "status",
        enableSorting: false,
        enableColumnFilter: true,
        filterVariant: "select",
        filterSelectOptions: statusFilterOptions,
        muiTableBodyCellProps: {
          sx: {
            minWidth: 0,
            overflow: "hidden",
          },
        },
        Cell: ({ table, row }) => renderStatusColumn(table, row.original),
      }),
      columnHelper.display({
        header: "Name",
        id: "name",
        enableSorting: true,
        enableColumnFilter: true,
        muiTableBodyCellProps: {
          sx: {
            minWidth: 0,
            overflow: "hidden",
          },
        },
        Cell: ({ row, table }) => {
          if (table.getState().isLoading) return;
          const value = !isProposalLoadIssue(row.original) && row.original.name?.value;
          if (!value) return <Icon>remove</Icon>;
          return (
            <RowTitle>
              <RowLink to={proposalDestination(row.original)}>{value}</RowLink>
            </RowTitle>
          );
        },
      }),
      columnHelper.display({
        header: "Description",
        id: "description",
        enableSorting: false,
        enableColumnFilter: true,
        Cell: DescriptionCell,
      }),
      columnHelper.display({
        header: "Creator",
        id: "creator",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ table, row }) => renderCreatorColumn(table, row.original),
      }),
      columnHelper.display({
        header: "Negotiable",
        id: "negotiable",
        enableSorting: false,
        enableColumnFilter: true,
        size: 125,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: "Negotiable" },
          { value: "false", label: "Non-negotiable" },
        ],
        Cell: NegotiableCell,
      }),
      columnHelper.display({
        header: "Team",
        id: "team",
        enableSorting: false,
        enableColumnFilter: false,
        size: teamColumnSize,
        Cell: ({ table, row }) => renderTeamColumn(table, row.original),
      }),
      columnHelper.display({
        header: "Fee Schedule",
        id: "feeSchedule",
        enableSorting: false,
        enableColumnFilter: false,
        size: 115,
        Cell: ({ table, row }) => renderFeeScheduleColumn(table, row.original),
      }),
      columnHelper.display({
        header: "Response Due",
        id: "responseDueBy",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
        Cell: ResponseDueByCell,
      }),
      columnHelper.display({
        header: "Start Date",
        id: "start",
        enableSorting: true,
        enableColumnFilter: false,
        size: 95,
        Cell: StartDateCell,
      }),
      columnHelper.display({
        header: "End Date",
        id: "end",
        enableSorting: true,
        enableColumnFilter: false,
        size: 95,
        Cell: EndDateCell,
      }),
      columnHelper.display({
        header: "Hire Date",
        id: "hireDate",
        enableSorting: false,
        enableColumnFilter: false,
        size: 95,
        Cell: HireDateCell,
      }),
      columnHelper.display({
        header: "Archive Date",
        id: "deleted",
        enableSorting: true,
        enableColumnFilter: false,
        size: 115,
        Cell: ArchiveDateCell,
      }),
    ],
    [
      proposalDestination,
      renderStatusColumn,
      renderCreatorColumn,
      renderTeamColumn,
      renderFeeScheduleColumn,
      teamColumnSize,
    ],
  );

  const openProposal = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal)) {
        console.warn("Cannot open proposal with loading issues.");
        return;
      }
      const destination = proposalDestination(proposal);
      if (!destination) {
        console.error("Proposal ID is required");
        return;
      }
      navigate(destination);
    },
    [navigate, proposalDestination],
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitleContainer>
            <TableTitle variant="h4">Proposals</TableTitle>
            {isMediumDisplaySize && !session.isRestrictedOnboarding && (
              <Tooltip title={createProposalTooltip}>
                {/* A span keeps the tooltip reachable while the button inside it is disabled. */}
                <span>
                  <NewButton
                    color="primary"
                    disabled={!canCreateProposal}
                    onClick={() => navigate("/proposals/drafts/create")}
                  >
                    <AddBoxIcon fontSize="large" />
                  </NewButton>
                </span>
              </Tooltip>
            )}
          </TableTitleContainer>
          <LowerTopToolbarContainer>
            {!session.isRestrictedOnboarding && <ProposalTabs
              value={isChoosingPageTab ? false : effectivePageTab}
              indicatorColor="primary"
              textColor="primary"
              variant={isMediumDisplaySize ? "scrollable" : "standard"}
              scrollButtons={isMediumDisplaySize ? "auto" : false}
              allowScrollButtonsMobile
              onChange={handlePageTabChange}
            >
              <Tab
                value={PageTab.Active}
                label={
                  <NumberBadge
                    isLoading={activeProposalCountQuery.isLoading}
                    tooltip={`${activeProposalCountQuery.data ?? 0} active proposal(s)`}
                    badgeCount={activeProposalCountQuery.data ?? 0}
                    color="info"
                    text={PageTab.Active}
                  />
                }
              />
              <Tab
                value={PageTab.Drafts}
                label={
                  <NumberBadge
                    isLoading={draftProposalCountQuery.isLoading}
                    tooltip={`${draftProposalCountQuery.data ?? 0} draft proposal(s)`}
                    badgeCount={draftProposalCountQuery.data ?? 0}
                    color="info"
                    text={PageTab.Drafts}
                  />
                }
              />
              <Tab
                value={PageTab.Archived}
                label={
                  <NumberBadge
                    isLoading={archivedProposalCountQuery.isLoading}
                    tooltip={`${archivedProposalCountQuery.data ?? 0} archived proposal(s)`}
                    badgeCount={archivedProposalCountQuery.data ?? 0}
                    text={PageTab.Archived}
                  />
                }
              />
            </ProposalTabs>}
            <FilterButtons>
              {effectivePageTab === PageTab.Active && (
                <>
                  <Chip
                    icon={<FilterListIcon />}
                    label="Inbox"
                    color="primary"
                    variant={currentBoxType === ProposalBoxType.Inbox ? "filled" : "outlined"}
                    onClick={() => handleChangeBoxType(ProposalBoxType.Inbox)}
                  />
                  <Chip
                    icon={<FilterListIcon />}
                    label="Outbox"
                    color="primary"
                    variant={currentBoxType === ProposalBoxType.Outbox ? "filled" : "outlined"}
                    onClick={() => handleChangeBoxType(ProposalBoxType.Outbox)}
                  />
                  <Chip
                    icon={<FilterListIcon />}
                    label="Pending Your Review"
                    color="primary"
                    variant={currentBoxType === ProposalBoxType.ActiveReview ? "filled" : "outlined"}
                    onClick={() => handleChangeBoxType(ProposalBoxType.ActiveReview)}
                  />
                </>
              )}
              {effectivePageTab === PageTab.Drafts && (
                <>
                  <Chip
                    icon={<FilterListIcon />}
                    label="Yours to Submit"
                    color="primary"
                    variant={currentBoxType === ProposalBoxType.DraftAuthored ? "filled" : "outlined"}
                    onClick={() => handleChangeBoxType(ProposalBoxType.DraftAuthored)}
                  />
                  <Chip
                    icon={<FilterListIcon />}
                    label="Pending Your Review"
                    color="primary"
                    variant={currentBoxType === ProposalBoxType.DraftReview ? "filled" : "outlined"}
                    onClick={() => handleChangeBoxType(ProposalBoxType.DraftReview)}
                  />
                </>
              )}
            </FilterButtons>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    [
      activeProposalCountQuery.data,
      activeProposalCountQuery.isLoading,
      archivedProposalCountQuery.data,
      canCreateProposal,
      createProposalTooltip,
      archivedProposalCountQuery.isLoading,
      currentBoxType,
      draftProposalCountQuery.data,
      draftProposalCountQuery.isLoading,
      effectivePageTab,
      handleChangeBoxType,
      handlePageTabChange,
      isChoosingPageTab,
      isMediumDisplaySize,
      navigate,
      session.isRestrictedOnboarding,
    ],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={props.table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={props.table} />
              <ShowHideColumnsButton table={props.table} />
              <ToggleFullScreenButton table={props.table} />
            </ToolbarButtons>
            <Tooltip title={createProposalTooltip}>
              {/* A span keeps the tooltip reachable while the button inside it is disabled. */}
              <span>
                <Button
                  startIcon={<AddBoxIcon />}
                  disabled={session.isRestrictedOnboarding || !canCreateProposal}
                  onClick={() => navigate("/proposals/drafts/create")}
                >
                  Create Proposal
                </Button>
              </span>
            </Tooltip>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    [
      canCreateProposal,
      createProposalTooltip,
      isMediumDisplaySize,
      navigate,
      session.isRestrictedOnboarding,
    ],
  );

  const isPreparingProposalsQuery =
    !sessionPermissions || isChoosingPageTab || (Boolean(session.user?.legalEntityId) && !proposalsQueryParams);
  const isTableLoading = isPreparingProposalsQuery || proposalsAPIQuery.isFetching;
  const showTableSkeletons = !tableProposals && isTableLoading;
  const skeletonProposals = useMemo(
    () =>
      Array.from({ length: pagination.pageSize }, (_, index) => ({
        id: Guid.generate(),
        issue: `Loading proposal ${index}`,
      })),
    [pagination.pageSize],
  );
  const tableData = useMemo(
    () => (showTableSkeletons ? skeletonProposals : (tableProposals ?? [])),
    [showTableSkeletons, skeletonProposals, tableProposals],
  );
  const tableRowCount = getTableRowCount({
    headers: tableProposalsHeaders,
    isShowingSkeletons: showTableSkeletons,
    pageSize: pagination.pageSize,
    tableProposalCount: tableProposals?.length,
  });

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<Proposal | ProposalLoadIssue> }) => {
      const isLoadIssueRow = !showTableSkeletons && isProposalLoadIssue(row.original);
      const proposalTableContext =
        !showTableSkeletons && !isProposalLoadIssue(row.original)
          ? getProposalTableContext(row.original)
          : undefined;

      return {
        "data-proposal-table-context": proposalTableContext,
        onClick: showTableSkeletons ? undefined : () => openProposal(row.original),
        sx: (theme: Theme) => {
          const contextRowStyles = getProposalTableContextRowStyles(theme, proposalTableContext);
          return {
            ...contextRowStyles,
            backgroundColor: isLoadIssueRow
              ? "rgba(255, 100, 100, 0.1)"
              : (contextRowStyles.backgroundColor ?? "inherit"),
            cursor: showTableSkeletons || isLoadIssueRow ? "default" : "pointer",
            animation:
              !showTableSkeletons &&
              row.original.id &&
                !prevBulkDeleteFailureIdValues.includes(row.original.id.value) &&
                bulkDeleteFailureIdValues.includes(row.original.id.value)
                ? `${failedOperationKeyFrame} 1s ease-in-out`
                : "none",
            animationIterationCount:
              row.original.id && bulkDeleteFailureIdValues.includes(row.original.id.value) ? "3" : "0",
          };
        },
      };
    },
    [bulkDeleteFailureIdValues, openProposal, prevBulkDeleteFailureIdValues, showTableSkeletons],
  );

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (proposalsAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
    return {
      action: (
        <AlertActions>
          <Button color="error" startIcon={<DeleteIcon />} onClick={handleBulkDraftProposalDelete}>
            Delete
          </Button>
        </AlertActions>
      ),
      sx: (theme: Theme) => ({
        "& .MuiBox-root .MuiStack-root": {
          alignItems: "baseline",
          gap: theme.spacing(1),
        },
      }),
    };
  }, [proposalsAPIQuery.isError, handleBulkDraftProposalDelete]);

  const getRowId = useCallback((row: Proposal | ProposalLoadIssue) => {
    if (isProposalLoadIssue(row)) return `load-issue-${row.id.value}`;
    return row.id?.value ?? "";
  }, []);

  const columnVisibility = useMemo(
    () => ({
      ...userColumnVisibility,
      hireDate: effectivePageTab === PageTab.Archived ? (userColumnVisibility.hireDate ?? true) : false,
      deleted: effectivePageTab === PageTab.Archived ? (userColumnVisibility.deleted ?? true) : false,
    }),
    [effectivePageTab, userColumnVisibility],
  );

  const muiSelectCheckboxProps = useCallback((
    props: { row: MRT_Row<Proposal | ProposalLoadIssue> }): CheckboxProps => {
    const { row } = props;
    return {
      onClick: (event) => {
        event.stopPropagation();
        if (row.original.id && bulkDeleteFailureIdValues.includes(row.original.id.value)) {
          event.preventDefault();
        }
      },
      title:
        row.original.id && bulkDeleteFailureIdValues.includes(row.original.id.value)
          ? "Cannot delete this proposal"
          : undefined,
    };
  }, [bulkDeleteFailureIdValues]);

  const tableOptions = useMemo<MRT_TableOptions<Proposal | ProposalLoadIssue>>(() => {
    return {
      columns,
      data: tableData,
      getRowId,
      enableRowSelection:
        !showTableSkeletons && effectivePageTab === PageTab.Drafts
          ? (row) => !!row.original.id && !bulkDeleteFailureIdValues.includes(row.original.id.value)
          : false,
      muiSelectCheckboxProps,
      enableRowActions: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      enableColumnPinning: true,
      initialState: {
        columnOrder: [
          "status",
          "name",
          "description",
          "creator",
          "negotiable",
          "team",
          "feeSchedule",
          "responseDueBy",
          "start",
          "end",
          "hireDate",
          "deleted",
        ],
        columnPinning: { right: ["mrt-row-actions"] },
        showGlobalFilter: true,
      },
      manualFiltering: true,
      enableColumnFilters: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      layoutMode: "grid",
      muiTableContainerProps: {
        sx: {
          flex: 1,
          minWidth: 0,
          position: "relative"
        },
      },
      muiTableHeadCellProps: {
        sx: {
          "& .Mui-TableHeadCell-Content": {
            minWidth: 0,
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            lineHeight: 1.2,
            overflow: "visible",
            overflowWrap: "normal",
            textOverflow: "clip",
            whiteSpace: "normal",
            wordBreak: "normal",
          },
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden"
        },
      },
      muiTopToolbarProps: {
        sx: {
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          "& > .MuiBox-root": {
            gap: 0,
            minWidth: 0,
            overflow: "hidden",
          },
        },
      },
      muiTableBodyProps: {
        sx: {
          height: "100%",
          "& tr:only-of-type td[colspan]": {
            height: "100%",
            verticalAlign: "middle",
            padding: 0,
            borderBottom: "none",
          },
        },
      },
      muiSearchTextFieldProps: {
        placeholder: `Search ${effectivePageTab.toLowerCase()}`,
        slotProps: {
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: globalFilter ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => setGlobalFilter("")}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        },
      },
      muiToolbarAlertBannerProps,
      rowCount: tableRowCount,
      pageCount: Math.ceil(tableRowCount / pagination.pageSize),
      enablePagination: true,
      renderEmptyRowsFallback: emptyRowsFallback({
        isError: proposalsAPIQuery.isError,
        isLoading: isTableLoading,
        onRetry: () => void proposalsAPIQuery.refetch(),
        // The tab is the noun: "No drafts" would be the tab's name, not the thing it holds.
        subject:
          effectivePageTab === PageTab.Drafts
            ? "draft proposals"
            : effectivePageTab === PageTab.Archived
              ? "archived proposals"
              : "active proposals",
      }),
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      onColumnVisibilityChange: setUserColumnVisibility,
      positionGlobalFilter: "none",
      state: {
        columnVisibility,
        columnFilters,
        density: isCompactTeamColumn ? "compact" : "comfortable",
        globalFilter,
        isLoading: isTableLoading,
        pagination,
        showAlertBanner: Boolean(proposalsAPIQuery.isError),
        showGlobalFilter: true,
        showProgressBars: proposalsAPIQuery.isFetching,
        sorting,
        rowSelection,
        showSkeletons: showTableSkeletons,
      },
    };
  }, [
    columns, 
    tableData, 
    tableRowCount, 
    effectivePageTab, 
    columnVisibility,
    muiSelectCheckboxProps, 
    muiTableBodyRowProps, 
    renderToolbarInternalActions, 
    renderTopToolbarCustomActions, 
    muiToolbarAlertBannerProps, 
    pagination, 
    columnFilters, 
    globalFilter, 
    isTableLoading, 
    isCompactTeamColumn, 
    proposalsAPIQuery, 
    sorting, 
    rowSelection, 
    bulkDeleteFailureIdValues, 
    getRowId, 
    showTableSkeletons, 
    setColumnFilters, 
    setGlobalFilter, 
    setPagination, 
    setSorting
  ]);

  const onboardingState = session.onboardingWork?.state;
  if (
    session.isRestrictedOnboarding &&
    onboardingState &&
    onboardingState !== "AgreementRequired" &&
    onboardingState !== "ProposalInProgress" &&
    onboardingState !== "Completed"
  ) {
    return <OnboardingProposalStatePage state={onboardingState} />;
  }

  if (!sessionPermissions) return <MRTNoMemo<Proposal | ProposalLoadIssue> options={tableOptions} />;
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;

  return <MRTNoMemo<Proposal | ProposalLoadIssue> options={tableOptions} />;
}

function getGlobalFilterFromSearchParams(searchParams: URLSearchParams): string {
  return searchParams.get("query") ?? "";
}

function getColumnFiltersFromSearchParams(searchParams: URLSearchParams): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  const status = searchParams.get("status");
  const name = searchParams.get("name");
  const description = searchParams.get("description");
  const negotiable = searchParams.get("negotiable");

  if (status) filters.push({ id: "status", value: status });
  if (name) filters.push({ id: "name", value: name });
  if (description) filters.push({ id: "description", value: description });
  if (negotiable !== null) filters.push({ id: "negotiable", value: negotiable });

  return filters;
}

function getSortingFromSearchParams(searchParams: URLSearchParams): SortingState {
  const orderByParam = searchParams.get("orderBy")?.toLowerCase();
  const orderBy = Object.values(ProposalOrderField).find((field) => field.toLowerCase() === orderByParam);
  if (!orderBy) return [];

  const orderDirParam = searchParams.get("orderDir")?.toLowerCase();
  const orderDir = Object.values(OrderDirection).find((direction) => direction.toLowerCase() === orderDirParam);

  return [{
    id: orderBy.toLowerCase(),
    desc: orderDir !== OrderDirection.Ascending,
  }];
}

function getPaginationFromSearchParams(searchParams: URLSearchParams): PaginationState {
  const page = Number.parseInt(searchParams.get("page") ?? "", 10);
  const pageSize = Number.parseInt(searchParams.get("pageSize") ?? "", 10);

  return {
    pageIndex: Number.isFinite(page) && page > 0 ? page - 1 : PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : PaginationParameters.DEFAULT_PAGE_SIZE,
  };
}

function getSearchParamsFromTableState(
  state: ProposalTableSearchState,
  currentSearchParams: URLSearchParams,
): URLSearchParams {
  const { globalFilter, columnFilters, sorting, pagination } = state;
  const newSearchParams = new URLSearchParams();
  if (globalFilter) {
    newSearchParams.set("query", globalFilter);
  }

  const statusFilter = columnFilters.find((filter) => filter.id === "status")?.value as string | undefined;
  if (statusFilter) {
    newSearchParams.set("status", statusFilter);
  }

  const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
  if (nameFilter) {
    newSearchParams.set("name", nameFilter);
  }

  const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
  if (descriptionFilter) {
    newSearchParams.set("description", descriptionFilter);
  }

  const negotiableFilter = columnFilters.find((filter) => filter.id === "negotiable")?.value as string | undefined;
  if (negotiableFilter !== undefined) {
    newSearchParams.set("negotiable", negotiableFilter);
  }

  const orderingField = sorting.find((sort) =>
    Object.values(ProposalOrderField)
      .map((field) => field.toLowerCase())
      .includes(sort.id.toLowerCase()),
  );

  if (orderingField) {
    const orderBy = Object.values(ProposalOrderField).find(
      (field) => field.toLowerCase() === orderingField.id.toLowerCase(),
    );
    if (orderBy) newSearchParams.set("orderBy", orderBy);
    newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
  }

  const paginationPageIndex = pagination.pageIndex ?? 0;
  newSearchParams.set("page", (paginationPageIndex + 1).toString());
  const paginationPageSize = pagination.pageSize ?? PaginationParameters.DEFAULT_PAGE_SIZE;
  newSearchParams.set("pageSize", paginationPageSize.toString());

  const boxType = currentSearchParams.get("boxType");
  if (isProposalBoxType(boxType)) newSearchParams.set("boxType", boxType);

  return newSearchParams;
}

function applyUpdater<T>(updater: MRT_Updater<T>, previous: T): T {
  return typeof updater === "function" ? (updater as (previous: T) => T)(previous) : updater;
}

type ProposalTableContextRowStyles = {
  backgroundColor?: string;
  [selector: string]: unknown;
};

function getProposalTableContextRowStyles(
  theme: Theme,
  context?: ProposalTableContext,
): ProposalTableContextRowStyles {
  if (!context) return {};

  const config = proposalTableContextStyleConfig[context];
  const contextColor = getProposalTableContextColor(theme, config.palette);
  const borderColor = config.borderPalette ? theme.palette[config.borderPalette].main : undefined;

  return {
    backgroundColor: alpha(contextColor, config.backgroundOpacity),
    "& > td": {
      backgroundColor: "inherit",
    },
    ...(borderColor
      ? {
          "& > td:first-of-type": {
            boxShadow: `inset 3px 0 0 ${borderColor}`,
          },
        }
      : {}),
    "&:hover": {
      backgroundColor: alpha(contextColor, config.hoverOpacity),
    },
    "&.Mui-selected": {
      backgroundColor: alpha(contextColor, config.selectedOpacity),
    },
    "&.Mui-selected:hover": {
      backgroundColor: alpha(contextColor, config.selectedHoverOpacity),
    },
  };
}

function getProposalTableContextColor(
  theme: Theme,
  palette: "error" | "info" | "success" | "text",
): string {
  if (palette === "text") return theme.palette.text.primary;
  return theme.palette[palette].main;
}

function getTableRowCount({
  headers,
  isShowingSkeletons,
  pageSize,
  tableProposalCount,
}: {
  headers?: PaginationHeaders;
  isShowingSkeletons: boolean;
  pageSize: number;
  tableProposalCount?: number;
}): number {
  if (isShowingSkeletons) return pageSize;
  return headers?.totalElements ?? tableProposalCount ?? 0;
}

const DescriptionCell = ({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) => {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.description?.value;
  return value ? <EllipsisCell variant="body2">{value}</EllipsisCell> : <Icon>remove</Icon>;
};

const NegotiableCell = ({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) => {
  if (table.getState().isLoading) return;
  let value: boolean | undefined;
  if (!isProposalLoadIssue(row.original)) {
    value = row.original.negotiable?.value;
  }
  if (value === undefined) {
    return <Icon>remove</Icon>;
  }
  return value ? <CheckIcon /> : <CloseIcon />;
};

function ResponseDueByCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.responseDueBy;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function StartDateCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.startDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function EndDateCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.endDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function HireDateCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.hireDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function ArchiveDateCell({ row, table }: {
  row: { original: Proposal | ProposalLoadIssue },
  table: MRT_TableInstance<Proposal | ProposalLoadIssue>
}) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.archiveDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}
